import type { RouteRecordRaw } from 'vue-router';

import { GlobalState } from '@/global';
import { wrapRuntimeImport } from '@/shared/helpers/async.helpers';
import { makePermissionMeta } from '@/shared/helpers/permissions.helpers';

import { canEditAllBookings, canEditOwnBookings } from './subapps/bookings/permissions';
import { SalesConfigRoutes } from './subapps/config/routes';
import { canAccessFullSalespoint } from './subapps/salespoint/permissions';

export const SalesRoutes: RouteRecordRaw[] = [
    {
        path: '',
        // Full-salespoint users land on the register; booking-only users (e.g. edit-own stylists)
        // go straight to bookings rather than the bookings-only salespoint.
        redirect: () => (canAccessFullSalespoint.value ? '/sales/salespoint' : '/sales/bookings')
    },
    {
        path: 'gift-cards',
        component: wrapRuntimeImport(() => import('./subapps/gift-cards/gift-cards.vue')),
        meta: { ...makePermissionMeta('sales', 'giftCardsView'), subAppName: 'Gift Cards' }
    },
    {
        path: 'reports',
        component: wrapRuntimeImport(() => import('./subapps/reports/reports.vue')),
        meta: { ...makePermissionMeta('sales', 'reportsView'), subAppName: 'Reports' }
    },
    {
        path: 'cash-sessions',
        component: wrapRuntimeImport(() => import('./subapps/cash-sessions/cash-sessions.vue')),
        meta: { ...makePermissionMeta('sales', 'reportsView'), subAppName: 'Cash Sessions' }
    },
    {
        path: 'closeouts',
        component: wrapRuntimeImport(() => import('./subapps/closeouts/closeouts.vue')),
        meta: { ...makePermissionMeta('sales', 'reportsView'), subAppName: 'Closeouts' }
    },
    {
        path: 'bookings',
        component: wrapRuntimeImport(() => import('./subapps/bookings/bookings.vue')),
        meta: { ...makePermissionMeta('sales', 'bookingsViewOwn'), subAppName: 'Bookings' },
        redirect: '/sales/bookings/calendar',
        children: [
            {
                path: 'calendar',
                component: wrapRuntimeImport(() => import('./subapps/bookings/calendar.vue')),
                meta: makePermissionMeta('sales', 'bookingsViewOwn')
            },
            {
                path: 'list',
                component: wrapRuntimeImport(() => import('./subapps/bookings/list.vue')),
                meta: makePermissionMeta('sales', 'bookingsViewOwn')
            },
            {
                path: 'waitlist',
                component: wrapRuntimeImport(() => import('./subapps/bookings/waitlist.vue')),
                meta: makePermissionMeta('sales', 'bookingsViewOwn')
            }
        ]
    },
    {
        path: 'config',
        children: SalesConfigRoutes
    },
    {
        path: 'salespoint',
        component: wrapRuntimeImport(() => import('./subapps/salespoint/salespoint.vue')),
        meta: {
            ...makePermissionMeta('sales', () => {
                if (canEditAllBookings.value) return 'bookingsEditAll';
                if (canEditOwnBookings.value) return 'bookingsEditOwn';
                return GlobalState.isWorkstation ? 'sp' : 'spWithoutInstall';
            }),
            subAppName: 'Salespoint'
        }
    },
    {
        path: 'transactions',
        component: wrapRuntimeImport(() => import('./subapps/transactions/transactions.vue')),
        meta: makePermissionMeta('sales', 'transactionsView')
    },
    {
        path: 'fulfillment',
        // same as transactions, transactions will internally detect which mode to use
        component: wrapRuntimeImport(() => import('./subapps/transactions/transactions.vue')),
        meta: makePermissionMeta('sales', 'fulfillmentView')
    },
    {
        path: 'fulfillment/pickup-scan',
        component: wrapRuntimeImport(() => import('./subapps/fulfillment/pickup-scan.vue')),
        meta: makePermissionMeta('sales', 'fulfillmentEdit')
    }
];
